import { useCallback, useEffect, useRef } from 'react';

import { IntentMode } from '../../../../common/utils/intent-detection';
import { useDebouncedCallback } from '../../../../common/utils/use-debounced-callback';

import { useIntentDetectionService } from './use-intent-detection-service';

const DEBOUNCE_MS = 250;

/**
 * Silent intent detection hook for the control cohort.
 *
 * We need intent detection analytics from the control cohort so we
 * can establish a statistically significant baseline for comparison with
 * the test cohort. Without this, we cannot measure whether intent
 * detection is positively impacting search success metrics.
 * See: https://hello.atlassian.net/wiki/spaces/jirasearch/pages/6613471377/baseline+and+treatment+in+Jira+FPS+search
 *
 * Calls the intent detection API and fires the `intentDetectionResponse`
 * analytics event + VitalStats network events, but does NOT:
 * - Update any UI state (no primaryMode, no FlipList reordering)
 * - Fire UX VitalStats events (those remain test-only)
 * - Render any intent router rows
 *
 * This mirrors the data-fetching side of `useIntentRouting` without the
 * UI state management.
 *
 * Delete this hook when cleaning up the
 * rovo_jira_nin_fps_nlq_navigation experiment. It exists purely for
 * metrics purposes.
 */
export const useSilentIntentDetection = ({
	query,
	minQueryLength,
}: {
	query: string;
	minQueryLength: number;
}): void => {
	const intentDetection = useIntentDetectionService({
		defaultMode: IntentMode.GNIN,
	});

	const latestQueryRef = useRef(query);
	latestQueryRef.current = query;

	// Track the last query we triggered detection for to avoid duplicate calls
	const lastDetectedQueryRef = useRef<string | null>(null);

	const runDetection = useCallback(async () => {
		const q = latestQueryRef.current;
		if (q.length < minQueryLength) {
			return;
		}

		// Fire-and-forget: call the API and emit events, discard the result
		await intentDetection(q);
	}, [intentDetection, minQueryLength]);

	const debouncedDetect = useDebouncedCallback(runDetection, DEBOUNCE_MS);

	const shouldRun = query.length >= minQueryLength;

	useEffect(() => {
		if (!shouldRun) {
			return;
		}
		// Skip if we already triggered detection for this exact query
		if (lastDetectedQueryRef.current === query) {
			return;
		}
		lastDetectedQueryRef.current = query;
		debouncedDetect();
		return () => {
			debouncedDetect.cancel();
		};
	}, [debouncedDetect, shouldRun, query]);
};
